import React from 'react';
import { Image, Text, View, StyleSheet } from '@react-pdf/renderer';

import logo from '../../logo.png';
import { readTenantBranding } from '../../utils/tenantBrandingCache';
import {
  resolvePdfHeaderFields,
  pdfLogoSrcFromBranding,
  resolveAffiliationServingLine,
} from '../../utils/pdfFooterBranding';
import {
  resolvePdfBrandColors,
  PDF_HEADER_LOGO_WIDTH,
  PDF_HEADER_LOGO_HEIGHT,
  PDF_HEADER_LOGO_COL_FRAC,
} from '../../utils/pdfBrandColors';
import Boxwith4Line from '../BoxWith4Line/BoxWith4Line';
import BoxWith4LineKeys from '../BoxWith4LineKeys/BoxWith4LineKeys';
import CustomerPackageDesTable from '../CustomerPackageDesTable/CustomerPackageDesTable';
import ConsignmentBox from '../ConsignmentBox/ConsignmentBox';
import AcknowledgementTable from '../AcknowledgementTable/AcknowledgementTable';
import AffliationBox from '../AffliationBox/AffliationBox';

const styles = StyleSheet.create({
  logo: {
    width: PDF_HEADER_LOGO_WIDTH,
    maxWidth: '100%',
    height: PDF_HEADER_LOGO_HEIGHT,
    objectFit: 'contain',
  },
});

const CustomerCopy = ({
  consignor,
  consignee,
  title,
  originAddress,
  destinationAddress,
  type,
  gcnno,
  carGcnno,
  date,
  phone,
  ocity,
  dcity,
  bolSignaturePng,
  bolSignerName,
  bolSignedAt,
  branding: brandingProp,
}) => {
  const branding = brandingProp ?? readTenantBranding();
  const { primary } = resolvePdfBrandColors(branding);
  const h = resolvePdfHeaderFields(branding);
  const logoSrc = pdfLogoSrcFromBranding(branding, logo);
  const addressPhoneLine = [h.addressOneLine, h.supportPhone].filter(Boolean).join('. ');
  const carrierName =
    (branding.companyName && String(branding.companyName).trim()) || 'ACE Relocations';

  const accentText = {
    fontSize: 10,
    color: primary,
    textAlign: 'right',
    width: '100%',
    lineHeight: 1.35,
  };

  return (
    <View>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          width: '100%',
          borderBottomWidth: 1,
          borderBottomColor: '#dddddd',
          marginBottom: 10,
          paddingBottom: 6,
        }}
        wrap={false}
      >
        <View style={{ width: `${Math.round(PDF_HEADER_LOGO_COL_FRAC * 100)}%`, flexShrink: 0 }}>
          <Image style={styles.logo} src={logoSrc} />
        </View>
        <View
          style={{
            width: `${100 - Math.round(PDF_HEADER_LOGO_COL_FRAC * 100)}%`,
            alignItems: 'flex-end',
            paddingLeft: 10,
          }}
        >
          <Text
            style={{
              fontSize: 18,
              fontFamily: 'Helvetica-Bold',
              color: primary,
              textAlign: 'right',
              marginBottom: 4,
              width: '100%',
            }}
          >
            {h.headerTagline}
          </Text>
          {addressPhoneLine ? <Text style={accentText}>{addressPhoneLine}</Text> : null}
          <Text style={[accentText, { marginTop: addressPhoneLine ? 2 : 0 }]}>
            E: {h.supportEmail}
            {'    '}
            W: {h.footerWebsite}
          </Text>
        </View>
      </View>
      <View
        style={{
          flexDirection: 'row',
          flexGrow: 1,
        }}
      >
        <View
          style={{
            height: '400px',
            width: '70%',
          }}
        >
          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              marginBottom: '10px',
            }}
          >
            <View style={{ width: '70%' }}>
              <Boxwith4Line
                accentColor={primary}
                title='Cognisor'
                address={originAddress}
                fullName={`${consignor ?.firstName} ${consignor ?.lastName}`}
              />
            </View>
            <View style={{ width: '30%', border: '1 1 1 0' }}>
              <BoxWith4LineKeys
                accentColor={primary}
                gcnNo={gcnno}
                date={date}
                from={ocity}
                to={dcity}
              />
            </View>
          </View>
          <View style={{ marginBottom: '10px' }}>
            <Boxwith4Line
              accentColor={primary}
              title='Consignee'
              address={destinationAddress}
              fullName={`${consignee ?.firstName} ${consignee ?.lastName}`}
            />
          </View>

          <CustomerPackageDesTable accentColor={primary} description={type} noOfPkg={' '} />

          <View>
            <Text style={{ marginTop: 2, color: primary, fontSize: 8, marginLeft: 2 }}>
              Note : Corrugated boxes, PVC boxes, Fiber drums & Moving blankets are property of{' '}
              {carrierName}
            </Text>
          </View>
        </View>
        <View
          style={{
            height: '400px',
            width: '30%',
            alignItems: 'center',
            padding: 3,
          }}
        >
          <Text style={{ color: primary, fontSize: 12 }}>{title}</Text>
          <ConsignmentBox carrierName={carrierName} />
          <AcknowledgementTable
            accentColor={primary}
            fullName={`${consignee ?.firstName} ${consignee ?.lastName}`}
            number={phone}
          />
          <AffliationBox accentColor={primary} servingLine={resolveAffiliationServingLine(branding)} />
          {title === 'CUSTOMER COPY' &&
          bolSignaturePng &&
          String(bolSignaturePng).startsWith('data:image/png') ? (
            <View
              style={{
                marginTop: 8,
                padding: 4,
                borderWidth: 1,
                borderColor: '#ccc',
                width: '100%',
                alignItems: 'flex-start',
              }}
              wrap={false}
            >
              <Text style={{ fontSize: 7, marginBottom: 3, fontWeight: 'bold' }}>
                Customer acknowledgment (Bill of Lading)
              </Text>
              <Image
                src={bolSignaturePng}
                style={{ width: 110, height: 44, objectFit: 'contain', marginBottom: 3 }}
              />
              <Text style={{ fontSize: 8 }}>{bolSignerName || '—'}</Text>
              {bolSignedAt ? (
                <Text style={{ fontSize: 7, color: '#333', marginTop: 2 }}>
                  {new Date(bolSignedAt).toLocaleString('en-IN')}
                </Text>
              ) : null}
            </View>
          ) : null}
          <View style={{ marginTop: 2 }}>
            <Text style={{ color: primary, fontSize: 8 }}>Subject to Ahmedabad jurisdiction</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export default CustomerCopy;
